import {
  createContext,
  useState,
  FunctionComponent,
  useContext,
  useCallback,
  useEffect,
  useMemo,
} from 'react'
import Firebase from '@/modules/Firebase/classes/Firebase'
import { IFirebaseAuthentication } from '@/modules/Firebase/firebase-types'
import { useRealtimeAttendanceContext } from '@/modules/RealtimeAttendance/contexts/RealtimeAttendanceContext'
import { useAuthContext } from '@/modules/Auth/contexts/AuthContext'
import {
  IMMEDIATE_NODE,
  SCHEDULED_NODE,
  NOSHOW_NODE,
} from '@/modules/Firebase/firebase-constants'
import usePageVisible from '@/components/Hooks/usePageVisible'

export interface IFirebaseContext {
  initializeFirebase(
    connectData: IFirebaseAuthentication,
    firebaseUserToken: string,
    clinicId: number,
    patientId: number,
  ): void
  logoutFromFirebase(): void
}

const FirebaseContext = createContext<IFirebaseContext>(null)

export const FirebaseProvider: FunctionComponent = ({ children }) => {
  const [firebase, setFirebase] = useState<Firebase>()
  const [isEnable, setIsEnable] = useState<boolean>(false)
  const { updateRealtimeAttendance } = useRealtimeAttendanceContext()
  const { user, updateUser } = useAuthContext()

  usePageVisible(visible => {
    firebase?.patientOnline(visible)
  })

  const initializeFirebase = useCallback(
    (
      connectData: IFirebaseAuthentication,
      firebaseUserToken: string,
      clinicId: number,
      patientId: number,
    ) => {
      const firebase = new Firebase(
        connectData,
        firebaseUserToken,
        clinicId,
        patientId,
        token => {
          updateUser(user => {
            user.firebaseToken = token
            return user
          })
        },
      )
      setFirebase(firebase)
      firebase.onAdd(data => {
        updateRealtimeAttendance(realtimeAttendance => {
          realtimeAttendance.setImmediateByFirebase(data)
          return realtimeAttendance
        })
      }, IMMEDIATE_NODE)
      firebase.onChange(data => {
        updateRealtimeAttendance(realtimeAttendance => {
          realtimeAttendance.setImmediateByFirebase(data)
          return realtimeAttendance
        })
      }, IMMEDIATE_NODE)
      firebase.onRemove(data => {
        updateRealtimeAttendance(realtimeAttendance => {
          realtimeAttendance.removeByFirebase(data, IMMEDIATE_NODE)
          return realtimeAttendance
        })
      }, IMMEDIATE_NODE)
      firebase.onAdd(data => {
        updateRealtimeAttendance(realtimeAttendance => {
          realtimeAttendance.setScheduledByFirebase(data)
          return realtimeAttendance
        })
      }, SCHEDULED_NODE)
      firebase.onChange(data => {
        updateRealtimeAttendance(realtimeAttendance => {
          realtimeAttendance.setScheduledByFirebase(data)
          return realtimeAttendance
        })
      }, SCHEDULED_NODE)
      firebase.onRemove(data => {
        updateRealtimeAttendance(realtimeAttendance => {
          realtimeAttendance.removeByFirebase(data, SCHEDULED_NODE)
          return realtimeAttendance
        })
      }, SCHEDULED_NODE)
      firebase.onAdd(data => {
        updateRealtimeAttendance(realtimeAttendance => {
          realtimeAttendance.setNoShowByFirebase(data)
          return realtimeAttendance
        })
      }, NOSHOW_NODE)
      firebase.onChange(data => {
        updateRealtimeAttendance(realtimeAttendance => {
          realtimeAttendance.setNoShowByFirebase(data)
          return realtimeAttendance
        })
      }, NOSHOW_NODE)
      firebase.onRemove(data => {
        updateRealtimeAttendance(realtimeAttendance => {
          realtimeAttendance.removeByFirebase(data, NOSHOW_NODE)
          return realtimeAttendance
        })
      }, NOSHOW_NODE)
      firebase.patientOnline(true)
    },
    [updateRealtimeAttendance, updateUser],
  )

  const logoutFromFirebase = useCallback(() => {
    setIsEnable(false)
    firebase.patientOnline(false)
    setFirebase(firebase => firebase.destroy())
  }, [firebase])

  useEffect(() => {
    if (user && !isEnable) {
      initializeFirebase(
        user.firebaseAuthentication,
        user.firebaseToken,
        user.clinic.id,
        user.patientId,
      )
      setIsEnable(true)
    }
  }, [user, isEnable, initializeFirebase])

  const firebaseValue = useMemo(() => {
    return {
      initializeFirebase,
      logoutFromFirebase,
    }
  }, [initializeFirebase, logoutFromFirebase])

  return (
    <FirebaseContext.Provider value={firebaseValue}>
      {children}
    </FirebaseContext.Provider>
  )
}

export const useFirebaseContext = (): IFirebaseContext => {
  const context = useContext(FirebaseContext)
  if (!context) {
    throw new Error('useAuthContext must be used within a FirebaseProvider')
  }
  return context
}
